import {
    Dispatch,
    SetStateAction,
    useContext,
    useEffect,
    useRef,
    useState
} from "react";
import { useTranslation } from "react-i18next";
import { AiOutlineClose } from "react-icons/ai";
import {
    checkNullAndUndefined,
    correctAmountWithDecimal
} from "../../Utilities/utils";
import { AuthContext } from "../AuthProvider";
import { BsCheckCircle, BsCheckCircleFill } from "react-icons/bs";
import Popup from "../booking/Popup";
import { Store } from "react-notifications-component";
import PopupInfo from "../booking/PopupInfo";
import {
    checkPaymentChargeWithCondition,
    createSinglePayIntent,
    getAllCards,
    getClubMemberships,
    handleRemoveCard,
    IPaymentIntent
} from "../../Api/api";
import { CardElement, useStripe } from "@stripe/react-stripe-js";
import { ICardPayments, ISinglePayIntentPayload } from "./CreditPackageModal";
import { ConfirmCardPaymentData } from "@stripe/stripe-js";
import OneTimeSuccessModal from "./OneTimeSuccessModal";

const PrivateMembershipCheckout = ({
    setOpenPrivateMembershipModal,
    membership,
    clubData,
    setClubMemberships,
    isLoading,
    setLoading
}: {
    setOpenPrivateMembershipModal: Dispatch<SetStateAction<boolean>>;
    membership: any;
    clubData: any;
    setClubMemberships: Dispatch<SetStateAction<any>>;
    isLoading: boolean;
    setLoading: Dispatch<SetStateAction<boolean>>;
}) => {
    const { t } = useTranslation();
    const currentUser: any = useContext(AuthContext);
    const stripe = useStripe();
    const [showCardPayment, setShowCardPayment] = useState(false);
    const [infoPopupTitle, setInfoPopupTitle] = useState("");
    const [infoPopupSubtitle, setInfoPopupSubtitle] = useState("");
    const [isLoadingCards, setIsLoadingCards] = useState(true);
    const [cardsFetching, setCardsFetching] = useState(true);
    const [savedCards, setSavedCards] = useState([]);
    const [selectedCard, setSelectedCard] = useState("");
    const [showSavedCards, setShowSavedCards] = useState(false);
    const [cardElement, setCardElement] = useState<any>(null);
    const [cardDetails, setCardDetails] = useState<any>();
    const [saveCardForFutureUse, setSaveCardForFutureUse] = useState(false);
    const [isOpen, setIsOpen] = useState(false);
    const [deleted, setDeleted] = useState(false);
    const [disablePayClick, setDisablePayClick] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");
    const [paymentResult, setPaymentResult] = useState<any>({});
    const [openOneTimeSuccessModal, setOpenOneTimeSuccessModal] =
        useState(false);
    const cardElementRef = useRef<any>(null);

    useEffect(() => {
        if (
            (cardsFetching && !savedCards.length) ||
            (cardsFetching && deleted)
        ) {
            const delay = 3000; // Delay in milliseconds (3 seconds)
            const timeoutId = setTimeout(() => {
                getAllCards(setCardsFetching)
                    .then((resp) => {
                        setCardsFetching(false);
                        const _activeCard = resp?.paymentMethods?.find(
                            (card: any) => card.defaultPaymentMethod
                        );
                        if (_activeCard) {
                            setSelectedCard(_activeCard.stripePaymentMethodId);
                            localStorage.setItem(
                                "activeCard",
                                JSON.stringify(_activeCard)
                            );
                        }

                        if (resp?.paymentMethods.length) {
                            setSavedCards(resp?.paymentMethods);
                            localStorage.setItem(
                                "savedCards",
                                JSON.stringify(resp?.paymentMethods)
                            );
                        } else {
                            setSavedCards([]);
                            localStorage.removeItem("savedCards");
                        }
                        setShowSavedCards(true);
                        setIsLoadingCards(false);
                        setDeleted(false);
                    })
                    .catch(() => setIsLoadingCards(false));
            }, delay);

            return () => clearTimeout(timeoutId);
        } else {
            setIsLoadingCards(false);
            setShowSavedCards(true);
        }
    }, [cardsFetching, savedCards, deleted]);

    useEffect(() => {
        if (showCardPayment && stripe) {
            setSelectedCard("");
            // const cardElement = elements.getElement(CardElement);
            // cardElementRef.current = cardElement;
        } else {
            cardElementRef.current = null;
            setCardDetails(null);
            setSaveCardForFutureUse(false);
        }
        handleCardElementSelect(cardElementRef.current);
        return () => {
            handleCardElementSelect(null);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [showCardPayment]);

    const handleCardElementSelect = (element: any) => {
        setCardElement(element);
    };

    const handleCardElementChange = (e: any) => {
        setCardDetails(e);
    };

    const handleSaveCardForFutureUseChange = (e: any) => {
        setSaveCardForFutureUse(!saveCardForFutureUse);
    };

    function calculatePercentage(amount: any, percentage: any) {
        if (
            checkNullAndUndefined(percentage) ||
            checkNullAndUndefined(amount)
        ) {
            return 0;
        }
        return (amount * percentage) / 100;
    }

    const fareAmountMembership = () => {
        let shareValue = membership?.price;
        let salesValue = 0;
        if (
            !checkNullAndUndefined(clubData?.salestax) &&
            clubData?.salestax?.salestax_on_top
        ) {
            salesValue = calculatePercentage(
                membership?.price,
                clubData?.salestax?.salestax_percentage
            );
            if (salesValue < 0) {
                salesValue = 0;
            }
        }
        shareValue = shareValue + salesValue;
        return shareValue;
    };

    const salesTaxAmount = () => {
        const value =
            Number(correctAmountWithDecimal(fareAmountMembership())) -
            Number(correctAmountWithDecimal(membership?.price));
        if (value < 0) {
            return 0;
        }
        return value;
    };

    const showPopupInfoBox = (title: string, subTitle: string) => {
        setInfoPopupTitle(title);
        setInfoPopupSubtitle(subTitle);
        setIsLoadingCards(true);
    };

    const handleDeleteCard = () => {
        const selectedPaymentCard: any = savedCards.find(
            (c: any) => c.stripePaymentMethodId === selectedCard
        );
        showPopupInfoBox(
            "Card removal",
            "Please wait while we remove your card..."
        );
        handleRemoveCard(selectedPaymentCard, savedCards).then((response) => {
            if (response?.data.success) {
                setIsOpen(!isOpen);
                Store.addNotification({
                    title: null,
                    message: "Card Removed Successfully!",
                    type: "success",
                    // insert: "center",
                    container: "bottom-center",
                    animationIn: ["animate__animated", "animate__fadeIn"],
                    animationOut: ["animate__animated", "animate__fadeOut"],
                    dismiss: {
                        duration: 2000
                    }
                });
                setCardsFetching(true);
                setDeleted(true);
            } else {
                setIsLoadingCards(false);
                Store.addNotification({
                    title: null,
                    message: "error in deleting card!",
                    type: "danger",
                    // insert: "center",
                    container: "bottom-center",
                    animationIn: ["animate__animated", "animate__fadeIn"],
                    animationOut: ["animate__animated", "animate__fadeOut"],
                    dismiss: {
                        duration: 2000
                    }
                });
            }
        });
    };

    const clearCardDetails = () => {
        setCardDetails(null);
        setSaveCardForFutureUse(false);
    };

    const confirmPaymentWith3DSecure = async (
        intent: IPaymentIntent | any,
        cardDetails?: any
    ) => {
        console.log("confirmPaymentWith3DSecure");
        if (!stripe) return;

        const confirmCardPaymentParams: ConfirmCardPaymentData = {};
        const localActiveCardString = localStorage.getItem("activeCard");
        const localActiveCard = localActiveCardString
            ? JSON.parse(localActiveCardString)
            : null;
        const paymentMethodId = localActiveCard?.stripePaymentMethodId;

        if (paymentMethodId && !cardDetails) {
            confirmCardPaymentParams.setup_future_usage = "off_session";
        }

        if (cardDetails && cardElement) {
            confirmCardPaymentParams.payment_method = {
                card: cardElement,
                billing_details: {
                    name: currentUser?.displayName,
                    email: currentUser?.email
                }
            };
        } else if (paymentMethodId) {
            confirmCardPaymentParams.payment_method = paymentMethodId; // only send for existing cards
        }

        const confirmPayment = await stripe.confirmCardPayment(
            intent?.clientSecret,
            confirmCardPaymentParams
        );
        if (confirmPayment) return confirmPayment;
    };

    const handleSingleCardPay = async ({
        payFor,
        gameType,
        clubId,
        totalPrice,
        sourceId,
        startTime = 0,
        endTime = 0,
        isSupportAutoJoinFlow = false,
        autoJoinAdditionalInfo,
        paymentMethod,
        discountCode,
        sourceType,
        position,
        membershipData
    }: ICardPayments) => {
        try {
            //   setLoadingPaymentStatus(true);

            //   const localActiveCard = localStorage.getItem('activeCard');
            //   const card: ICard = JSON.parse(localActiveCard || '{}');

            let createSinglePayIntentPayload: ISinglePayIntentPayload = {
                payFor,
                amount: Number(totalPrice.toFixed(2)),
                clubId,
                startTime,
                endTime,
                sourceId,
                gameType,
                isSupportAutoJoinFlow,
                autoJoinAdditionalInfo,
                paymentMethodType: paymentMethod,
                paymentMethod: paymentMethod,
                discountCode,
                sourceType,
                position,
                membershipData
            };

            if (saveCardForFutureUse && cardDetails) {
                if (!stripe) return;
                let _cardDetailsFromStripeTokenForIntent;
                if (cardDetails && cardElement) {
                    const { token, error } = await stripe.createToken(
                        cardElement
                    );
                    if (token && !error) {
                        _cardDetailsFromStripeTokenForIntent = {
                            last4: token.card?.last4,
                            expiryMonth: token.card?.exp_month,
                            expiryYear: Number(
                                token.card?.exp_year.toString().slice(-2)
                            ),
                            complete: true,
                            brand: token.card?.brand,
                            validExpiryDate: "Valid",
                            validNumber: "Valid",
                            validCVC: "Valid"
                        };
                    }
                }
                createSinglePayIntentPayload = {
                    ...createSinglePayIntentPayload,
                    cardDetails: _cardDetailsFromStripeTokenForIntent || cardDetails
                };
            } else if (saveCardForFutureUse && !cardDetails) {
                Store.addNotification({
                    title: null,
                    message: "Please fill up card details or select a card",
                    type: "danger",
                    container: "bottom-center",
                    animationIn: ["animate__animated", "animate__fadeIn"],
                    animationOut: ["animate__animated", "animate__fadeOut"],
                    dismiss: {
                        duration: 3000
                    }
                });
                return;
            }

            // getting intent
            let intent = null;
            intent = await createSinglePayIntent(createSinglePayIntentPayload);

            if (intent.clientSecret) {
                const paymentResponse: any = cardDetails
                    ? await confirmPaymentWith3DSecure(intent, cardDetails)
                    : await confirmPaymentWith3DSecure(intent);

                clearCardDetails();
                let checkPayForType = payFor === "subscription";

                if (paymentResponse) {
                    if (paymentResponse?.code === "Failed") {
                        const stripeMessage = paymentResponse?.stripeErrorCode;
                        return { code: "Failed", message: stripeMessage };
                    }
                    clearCardDetails();
                    let responseFromChargeCheck =
                        await checkPaymentChargeWithCondition(
                            intent?.paymentIntentId,
                            checkPayForType
                        );
                    if (responseFromChargeCheck) {
                        return {
                            ...responseFromChargeCheck.data,
                            paymentIntentId: intent?.paymentIntentId
                        };
                    }
                    return {
                        code: "Failed",
                        message: "Payment failed. Try again later."
                    };
                } else {
                    throw new Error(
                        "Failed to complete the payment. Try again"
                    );
                }
            } else {
                throw new Error("Failed to create payment intent");
            }
        } catch (error) {
            // @ts-ignore
            // throw error?.response?.data?.message;
            throw error;
        } finally {
            //   setLoadingPaymentStatus(false);
        }
    };

    // handling booking with card payment
    const handleCardPay = async () => {
        setDisablePayClick(true);

        try {
            const paymentRes = await handleSingleCardPay({
                clubId: clubData?._id,
                totalPrice: Number(
                    correctAmountWithDecimal(
                        correctAmountWithDecimal(fareAmountMembership())
                    )
                ),
                payFor: "subscription",
                sourceId: membership?._id,
                sourceType: "SUBSCRIPTION",
                isSupportAutoJoinFlow: true,
                autoJoinAdditionalInfo: {
                    salestax: clubData?.salestax
                },
                paymentMethod: "cards",
                discountCode: "",
                membershipData: null
            });

            if (!checkNullAndUndefined(paymentRes) && paymentRes) {
                if (paymentRes.code === "Failed") {
                    setErrorMessage(paymentRes.message);
                    return;
                }

                setPaymentResult(paymentRes);

                setLoading(true);
                try {
                    const membershipResp = await getClubMemberships(
                        clubData?._id
                    );
                    if (
                        membershipResp &&
                        membershipResp?.data?.memberships &&
                        membershipResp?.data?.memberships?.length
                    ) {
                        const filterData =
                            membershipResp?.data?.memberships?.filter(
                                (membership: any) => membership.hidden === false
                            );
                        setClubMemberships(filterData || []);
                    }
                } catch (error) {
                } finally {
                    setLoading(false);
                }

                setDisablePayClick(false);

                Store.addNotification({
                    title: null,
                    message: t("BookingModal.paymentSuccessful"),
                    type: "success",
                    container: "bottom-center",
                    animationIn: ["animate__animated", "animate__fadeIn"],
                    animationOut: ["animate__animated", "animate__fadeOut"],
                    dismiss: {
                        duration: 3000
                    }
                });

                setOpenOneTimeSuccessModal(true);
            }
        } catch (error: any) {
            setDisablePayClick(false);
            const msg = error?.response?.data?.message;
            setErrorMessage(msg);
        } finally {
            setDisablePayClick(false);
        }
    };

    return (
        <>
            {openOneTimeSuccessModal ? (
                <OneTimeSuccessModal
                    totalPayment={correctAmountWithDecimal(
                        correctAmountWithDecimal(fareAmountMembership())
                    )}
                    clubData={clubData}
                    membership={membership}
                    setOpenOneTimeSuccessModal={setOpenOneTimeSuccessModal}
                    setOpenPrivateMembershipModal={
                        setOpenPrivateMembershipModal
                    }
                    receiptUrl={paymentResult?.receiptUrl}
                />
            ) : (
                <div className="bg-white font-semibold rounded w-full max-w-lg sm:min-w-[512px] mx-auto z-[5100]">
                    <div className="text-white bg-secondary py-5 flex items-center justify-between flex-wrap gap-4 md:rounded-t text-2xl font-semibold px-5">
                        <h4
                            id="enterCode"
                            className="text-2xl lg:text-3xl font-semibold"
                        >
                            {t("payNow")}
                        </h4>
                        <button
                            className="btn"
                            onClick={() => {
                                setOpenPrivateMembershipModal(false);
                            }}
                        >
                            <AiOutlineClose className="pointer-events-none touch-none w-6 h-6 lg:w-8 lg:h-8 text-[#FF3A3A] hover:scale-110 transform duration-200" />
                        </button>
                    </div>
                    <>
                        <Popup
                            onDelete={handleDeleteCard}
                            isOpen={isOpen}
                            setIsOpen={setIsOpen}
                            title={"Delete Card"}
                            message={
                                "Are you sure you want to delete the selected card?"
                            }
                        />
                        {isLoadingCards && (
                            <PopupInfo
                                title={infoPopupTitle}
                                subTitle={infoPopupSubtitle}
                                isOpen={isLoadingCards}
                                setIsOpen={setIsLoadingCards}
                                isLoading={true}
                            />
                        )}
                        {(disablePayClick || isLoading) && (
                            <PopupInfo
                                title={`${t("processingThePayment")}...`}
                                subTitle={""}
                                isOpen={disablePayClick}
                                setIsOpen={setDisablePayClick}
                                isLoading={true}
                            />
                        )}
                    </>
                    <div className="overflow-y-auto p-5 relative text-sm font-normal max-h-[80vh]">
                        {showCardPayment && stripe && (
                            <>
                                <div className="flex flex-col mt-6 w-full overflow-y-auto p-5 relative text-sm font-normal">
                                    <div className="w-full md:max-w-[585px] h-full flex items-center mb-6">
                                        <div className="border-2 border-[#00000033] rounded-[8px] p-3 text-lg w-full md:max-w-[585px] outline-primary min-h-[50px] flex items-center">
                                            <CardElement
                                                className="w-full"
                                                onChange={
                                                    handleCardElementChange
                                                }
                                                options={{
                                                    hidePostalCode: true
                                                }}
                                                onReady={(element) => {
                                                    cardElementRef.current =
                                                        element;
                                                    handleCardElementSelect(
                                                        cardElementRef.current
                                                    );
                                                }}
                                            />
                                        </div>
                                    </div>
                                    <div className="flex align-middle gap-1.5 mt-2">
                                        <input
                                            type="checkbox"
                                            checked={saveCardForFutureUse}
                                            name="saveCardDetails"
                                            id="saveCardDetails"
                                            onChange={
                                                handleSaveCardForFutureUseChange
                                            }
                                        />
                                        <label htmlFor="saveCardDetails">
                                            Save card details for future use.
                                        </label>
                                    </div>
                                    <br />
                                    <div className="flex md:justify-start justify-center gap-2 items-center">
                                        <button
                                            className="text-base outline-none bg-white border border-secondary py-1 text-secondary px-2 rounded-lg w-[150px] hover:bg-red-500 hover:text-white hover:border-none transform duration-200"
                                            onClick={() =>{
                                                setShowCardPayment(false);
                                                setSaveCardForFutureUse(false);
                                                const localActiveCardString = localStorage.getItem("activeCard");
                                                const localActiveCard = localActiveCardString
                                                    ? JSON.parse(localActiveCardString)
                                                    : null;
                                                setSelectedCard(localActiveCard.stripePaymentMethodId);
                                            }}
                                        >
                                            {t("PaymentComponent.cancel")}
                                        </button>
                                    </div>
                                </div>
                            </>
                        )}
                        {showSavedCards && savedCards && savedCards.length ? (
                            <div className="flex flex-col mt-5 mb-3">
                                <h6 className="text-lg">
                                    {t("PaymentComponent.select")}
                                </h6>
                                <div className="flex flex-col mt-3">
                                    {savedCards?.map((card: any, index) => (
                                        <div
                                            id={`savedCard-${index}`}
                                            className={`border-y flex gap-3 flex-wrap justify-between items-center py-1 px-5 hover:cursor-pointer hover:transform hover:scale-[1.02] duration-200 ease-in-out ${
                                                selectedCard ===
                                                card.stripePaymentMethodId
                                                    ? "border-black"
                                                    : "border-gray-300"
                                            }`}
                                            key={card.stripePaymentMethodId}
                                            onClick={() => {
                                                setSelectedCard(
                                                    card.stripePaymentMethodId
                                                );
                                                localStorage.setItem(
                                                    "activeCard",
                                                    JSON.stringify(card)
                                                );
                                                setSaveCardForFutureUse(false);
                                            }}
                                        >
                                            <div className="flex items-center gap-3">
                                                <div>
                                                    {selectedCard ===
                                                    card.stripePaymentMethodId ? (
                                                        <BsCheckCircleFill />
                                                    ) : (
                                                        <BsCheckCircle />
                                                    )}
                                                </div>
                                                <div className="flex items-center gap-1 flex-wrap">
                                                    <h6 className="capitalize">
                                                        {card.brand}
                                                    </h6>
                                                    <p>
                                                        ( . . . .{" "}
                                                        {card.last4Digit} )
                                                    </p>
                                                </div>
                                            </div>

                                            <div>
                                                <button
                                                    id={`savedCardRemove-${index}`}
                                                    className="border border-black bg-white rounded-full text-sm py-1 px-4 hover:bg-secondary hover:text-white transform duration-200 ease-in-out"
                                                    onClick={() =>
                                                        setIsOpen(!isOpen)
                                                    }
                                                >
                                                    Remove
                                                </button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ) : isLoadingCards ? (
                            <h1 className="text-center h-full flex justify-center items-center mt-5">
                                {t("PaymentComponent.fetch")}...
                            </h1>
                        ) : (
                            <h1 className="text-center h-full flex justify-center items-center mt-5">
                                {t("PaymentComponent.notFound")}
                            </h1>
                        )}
                        {!showCardPayment && (
                            <div className="flex justify-center items-center">
                                <button
                                    className="text-base outline-none bg-secondary border border-secondary py-1 text-white px-2 rounded-lg w-[150px] mt-5 hover:bg-primary hover:border-primary hover:text-secondary transform duration-200"
                                    onClick={() => {
                                        setSelectedCard("");
                                        setShowCardPayment(true);
                                        setSaveCardForFutureUse(true);
                                    }}
                                >
                                    {t("PaymentComponent.addCard")}
                                </button>
                            </div>
                        )}
                    {!checkNullAndUndefined(clubData?.salestax) &&
                    clubData?.salestax?.salestax_on_top ? (
                        <div className="mt-8 text-xl">
                            <div className="w-full flex justify-between items-center flex-wrap gap-2">
                                <div>
                                    <p>
                                        {t("SubTotalComponent.subtotal")}:
                                    </p>
                                </div>
                                <div>
                                    <p className="font-medium">
                                        {membership?.price}{" "}
                                        {clubData?.currency}
                                    </p>
                                </div>
                            </div>
                            <div className="pt-2 w-full flex justify-between items-center flex-wrap gap-2">
                                <div>
                                    <p>{t("SubTotalComponent.tax")}:</p>
                                </div>
                                <div>
                                    <p className="font-medium">
                                        {correctAmountWithDecimal(
                                            salesTaxAmount()
                                        )}{" "}
                                        {clubData?.currency}
                                    </p>
                                </div>
                            </div>
                        </div>
                    ) : null}
                    <div
                        className={`${
                            !checkNullAndUndefined(clubData?.salestax) &&
                            clubData?.salestax?.salestax_on_top
                                ? ""
                                : "mt-8"
                        } pb-4 pt-2 w-full flex justify-between items-center flex-wrap gap-2 text-xl`}
                    >
                        <div>
                            <p>{t("SubTotalComponent.total")}:</p>
                        </div>
                        <div>
                            <p className="font-medium">
                                {correctAmountWithDecimal(
                                    correctAmountWithDecimal(
                                        fareAmountMembership()
                                    )
                                )}{" "}
                                {clubData?.currency}
                            </p>
                        </div>
                    </div>
                    {errorMessage && (
                        <p className="text-center text-red-500 text-sm">
                            {errorMessage}
                        </p>
                    )}
                    <div className="flex justify-center items-center my-4">
                        <button
                            disabled={disablePayClick}
                            onClick={handleCardPay}
                            className="bg-secondary w-full max-w-[300px] px-4 py-3 rounded-full text-white text-base"
                        >
                            <p>{t("payNow")}</p>
                        </button>
                    </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default PrivateMembershipCheckout;
